import { isEqual } from 'lodash'
import type { DropdownChangeEvent } from 'primereact/dropdown'
import { Dropdown as PrimeReactDropdown } from 'primereact/dropdown'
import { useCallback, useEffect, useId, useState } from 'react'
import type { DropdownOption } from '../multi-select-dropdown/MultiSelectDropdown'
import './Dropdown.scss'
import Skeleton from '../loading-spinner/Skeleton'

export interface DropdownProps {
  options: Array<DropdownOption>
  onChange: (selectedOption: DropdownOption | undefined) => void
  label: string
  placeholder: string
  selectedOption?: DropdownOption
  onOpen?: () => void
  isLoading?: boolean
  showClear?: boolean
  fullWidth?: boolean
  required?: boolean
  error?: boolean
  appendTo?: 'self' | HTMLElement
}

function Dropdown({
  options,
  selectedOption,
  onChange,
  label,
  placeholder,
  onOpen,
  isLoading = false,
  fullWidth = false,
  showClear = true,
  error = false,
  required = false,
  appendTo
}: DropdownProps) {
  const [selectedValue, setSelectedValue] = useState<string>()
  const id = useId()

  useEffect(() => {
    if (selectedOption) {
      if (!isEqual(selectedOption.value, selectedValue)) {
        setSelectedValue(selectedOption?.value)
      }
    } else {
      setSelectedValue(undefined)
    }
  }, [selectedOption])

  const onDropdownValueChange = useCallback(
    (event: DropdownChangeEvent) => {
      const newSelectedOption = options.find(option => option.value === event.value)
      setSelectedValue(newSelectedOption ? newSelectedOption.value : undefined)
      onChange(newSelectedOption)
    },
    [options]
  )

  const onShow = () => {
    //When onOpen is set, it will empty the option list
    if (onOpen) {
      options = []
      onOpen()
    }
  }

  const skeletonLoader = <Skeleton width='10rem' height='1.5rem' />

  const itemTemplate = (option: { value: string; text: string }) => {
    if (isLoading) return skeletonLoader

    return <>{option.text}</>
  }

  const emptyMessage = () => {
    if (isLoading) return skeletonLoader

    return 'No results found'
  }

  const dropdownClass = error ? 'p-invalid' : ''

  return (
    <div className='dropdown' style={{ width: fullWidth ? '100%' : undefined }}>
      <PrimeReactDropdown
        value={selectedValue}
        options={options}
        optionLabel='text'
        onChange={onDropdownValueChange}
        onShow={onShow}
        placeholder={placeholder}
        inputId={id}
        showClear={showClear}
        filter
        filterPlaceholder='Type to search...'
        itemTemplate={itemTemplate}
        emptyMessage={emptyMessage}
        className={dropdownClass}
        virtualScrollerOptions={{ itemSize: 32 }}
        appendTo={appendTo}
      />
      <label htmlFor={id} className='dropdown__label'>
        {label}
        {required && <span className='required'>*</span>}
      </label>
    </div>
  )
}

export default Dropdown
